import React from 'react';
import { Eye, CheckCircle, XCircle } from 'lucide-react';
import MesCommandesItemThumb from './MesCommandesItemThumb';
import MesCommandesRdvPaymentCell from './MesCommandesRdvPaymentCell';
import MesCommandesRdvDatesCell from './MesCommandesRdvDatesCell';
import { formatPhoneDisplay } from '../../utils/formatPhoneDisplay';
import { getRdvListPrestationLabel, getRdvNumeroDisplay } from '../../utils/rdvStatusUx';
import ProBadge from '../b2b/ProBadge';
import { MES_COMMANDES_PRESTATIONS_RDV_TABLE_REF_LABEL } from '../../utils/mesCommandesMixedUxCore';
import MesCommandesCommercantCell from './MesCommandesCommercantCell';
import {
  MesCommandesProductColGroup,
  MesCommandesRdvColGroup,
  MesCommandesPlatformProductColGroup,
  MesCommandesPlatformRdvColGroup,
  MES_CMD_ROW_ACTIONS_CLASS,
  mesCmdHeadClass,
  mesCmdTdActions,
  mesCmdTdClient,
  mesCmdTdCommercant,
  mesCmdTdDate,
  mesCmdTdDuree,
  mesCmdTdMain,
  mesCmdTdPaiement,
  mesCmdTdRef,
  mesCmdTdStatut,
  mesCmdTdTotal,
} from './mesCommandesMaquetteTableLayout';

const rowBase =
  'hover:bg-blue-50/40 cursor-pointer transition-colors border-b border-gray-100 last:border-b-0';

function ClientCell({ user }) {
  const name = [user?.nom, user?.prenom].filter(Boolean).join(' ').trim() || '—';
  const phone = user?.telephone ? formatPhoneDisplay(user.telephone) : null;
  return (
    <div className="min-w-0 max-w-full">
      <div
        className="font-bold text-gray-900 leading-snug text-[15px] line-clamp-2 break-words"
        title={name !== '—' ? name : undefined}
      >
        {name}
      </div>
      {phone ? (
        <div className="mt-1 text-xs text-gray-500 tabular-nums">{phone}</div>
      ) : (
        <div className="mt-1 text-[11px] text-gray-400 italic">Téléphone non renseigné</div>
      )}
    </div>
  );
}

/** Libellé liste Mes Commandes — EN_ATTENTE produit aligné sur RDV (affichage uniquement). */
function mesCommandesStatusLabel(statusDisplay, statut) {
  if (String(statut || '').toUpperCase() === 'EN_ATTENTE') {
    return 'En attente';
  }
  return statusDisplay?.label ?? '—';
}

function StatusPill({ statusDisplay, statut }) {
  const label = mesCommandesStatusLabel(statusDisplay, statut);
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-bold shadow-sm ${statusDisplay.color}`}
    >
      {statusDisplay.icon}
      <span>{label}</span>
    </span>
  );
}

function ReferenceNumero({ text }) {
  if (!text) return null;
  return (
    <div className="font-mono text-xs sm:text-sm text-gray-900 truncate leading-snug" title={text}>
      {text}
    </div>
  );
}

function RowActions({ commande, ctx, onOpenDetail, onProductStatus, onProductCancel, onRdvAction }) {
  const { isRdv, isPosComptoir, nextStatus, rdvActions, isUpdating, getNextStatusLabel, getRdvConfirmationMeta, getRdvActionIcon } =
    ctx;

  return (
    <div className={MES_CMD_ROW_ACTIONS_CLASS} onClick={(e) => e.stopPropagation()} role="presentation">
      <button
        type="button"
        className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-violet-200 text-violet-600 hover:bg-violet-50"
        title="Voir détails"
        onClick={() => onOpenDetail(commande)}
      >
        <Eye className="h-4 w-4" />
      </button>

      {!isRdv && nextStatus && commande.statut !== 'ANNULEE' && commande.statut !== 'LIVREE' ? (
        <button
          type="button"
          onClick={() => onProductStatus(commande, nextStatus)}
          className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-emerald-200 text-emerald-600 hover:bg-emerald-50 disabled:opacity-50"
          title={getNextStatusLabel(commande.statut, commande.modeRetrait, commande)}
          disabled={isUpdating}
        >
          <CheckCircle className="h-4 w-4" />
        </button>
      ) : null}

      {!isRdv && !isPosComptoir && commande.statut !== 'ANNULEE' && commande.statut !== 'LIVREE' ? (
        <button
          type="button"
          onClick={() => onProductCancel(commande)}
          className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-rose-200 text-rose-600 hover:bg-rose-50 disabled:opacity-50"
          title="Annuler"
          disabled={isUpdating}
        >
          <XCircle className="h-4 w-4" />
        </button>
      ) : null}

      {isRdv
        ? rdvActions.map((action) => {
            const meta = getRdvConfirmationMeta(commande, action.status);
            const { icon, className } = getRdvActionIcon(action.status);
            return (
              <button
                key={action.status}
                type="button"
                onClick={() => onRdvAction(commande, action.status, meta)}
                className={`inline-flex h-9 w-9 items-center justify-center rounded-full border border-gray-200 ${className} disabled:opacity-50`}
                title={meta.title}
                disabled={isUpdating}
              >
                {icon}
              </button>
            );
          })
        : null}
    </div>
  );
}

export function MesCommandesProductReferenceRow({
  commande,
  ctx,
  dimmed = false,
  showCommercantColumn = false,
  rowClassName,
  rowStyle,
  onOpenDetail,
  onProductStatus,
  onProductCancel,
  onRdvAction,
  renderPaiementCell,
}) {
  const {
    statusDisplay,
    typeBadge,
    totalAmount,
    totalBrut,
    finList,
    dateText,
    productImageUrl,
    extraProductCount,
    firstProductName,
    productListPrimaryText,
    productListPreviewText,
  } = ctx;

  return (
    <tr
      className={[rowBase, dimmed ? 'opacity-75' : '', rowClassName].filter(Boolean).join(' ')}
      style={rowStyle}
      onClick={() => onOpenDetail(commande)}
      title="Voir le détail de la commande"
    >
      <td className={mesCmdTdRef()}>
        <div className="min-w-0 max-w-full">
          <ReferenceNumero text={commande.numero || `#${commande.id}`} />
          <span
            className={`mt-1.5 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${typeBadge.className}`}
          >
            {typeBadge.icon}
            {typeBadge.label}
          </span>
          {typeBadge.timingLines?.length ? (
            <div
              className={`mt-1 text-[11px] leading-tight whitespace-pre-line ${
                typeBadge.kind === 'programmee' ? 'font-semibold text-amber-800' : 'text-gray-500'
              }`}
            >
              {typeBadge.timingLines.join('\n')}
            </div>
          ) : null}
          {commande.pricingMode === 'PRO' ? (
            <span className="mt-1 inline-flex">
              <ProBadge size="line" />
            </span>
          ) : null}
        </div>
      </td>
      <td className={mesCmdTdClient()}>
        <ClientCell user={commande.user} />
      </td>
      {showCommercantColumn ? (
        <td className={mesCmdTdCommercant()}>
          <MesCommandesCommercantCell commercant={commande.commercant} />
        </td>
      ) : null}
      <td className={mesCmdTdMain()}>
        <div className="flex items-center gap-3 min-w-0">
          <MesCommandesItemThumb
            variant="product"
            size="maquette"
            imageUrl={productImageUrl}
            extraProductCount={extraProductCount}
            alt={firstProductName || 'Produit'}
          />
          <div className="min-w-0">
            <div
              className="font-bold text-[15px] text-gray-900 uppercase leading-snug tracking-wide"
              title={productListPrimaryText || ''}
            >
              {productListPrimaryText || 'Commande produits'}
            </div>
            {productListPreviewText ? (
              <div
                className="text-xs text-gray-500 mt-0.5 leading-snug line-clamp-2 lowercase"
                title={productListPreviewText}
              >
                {productListPreviewText}
              </div>
            ) : null}
          </div>
        </div>
      </td>
      <td className={mesCmdTdTotal()}>
        <div className="font-bold text-lg text-gray-900">{ctx.formatEuro(totalAmount)}</div>
        {finList?.isPartiellementRembourse ? (
          <div className="mt-0.5 text-[11px] text-gray-500">Initial : {ctx.formatEuro(totalBrut)}</div>
        ) : null}
      </td>
      <td className={mesCmdTdStatut()}>
        <StatusPill statusDisplay={statusDisplay} statut={commande.statut} />
      </td>
      <td className={mesCmdTdPaiement()}>
        <div className="mes-cmd-payment-chip max-w-full [&_*]:text-xs [&_.rounded-lg]:shadow-sm">
          {renderPaiementCell(commande)}
        </div>
      </td>
      <td className={mesCmdTdDate()}>{dateText}</td>
      <td className={mesCmdTdActions()}>
        <RowActions
          commande={commande}
          ctx={ctx}
          onOpenDetail={onOpenDetail}
          onProductStatus={onProductStatus}
          onProductCancel={onProductCancel}
          onRdvAction={onRdvAction}
        />
      </td>
    </tr>
  );
}

export function MesCommandesRdvReferenceRow({
  commande,
  ctx,
  dimmed = false,
  showCommercantColumn = false,
  rowClassName,
  rowStyle,
  onOpenDetail,
  onProductStatus,
  onProductCancel,
  onRdvAction,
}) {
  const {
    statusDisplay,
    typeBadge,
    totalAmount,
    prestationImageUrl,
    prestationAccent,
    rdvDureeLabel,
    prestationSubtitle,
  } = ctx;
  const prestationLabel = getRdvListPrestationLabel(commande);

  return (
    <tr
      className={[rowBase, dimmed ? 'opacity-75' : '', rowClassName].filter(Boolean).join(' ')}
      style={rowStyle}
      onClick={() => onOpenDetail(commande)}
      title="Voir le détail du rendez-vous"
    >
      <td className={mesCmdTdRef()}>
        <div className="min-w-0 max-w-full">
          <ReferenceNumero text={getRdvNumeroDisplay(commande)} />
          <span
            className={`mt-1.5 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${typeBadge.className}`}
          >
            {typeBadge.icon}
            {typeBadge.label}
          </span>
        </div>
      </td>
      <td className={mesCmdTdClient()}>
        <ClientCell user={commande.user} />
      </td>
      {showCommercantColumn ? (
        <td className={mesCmdTdCommercant()}>
          <MesCommandesCommercantCell commercant={commande.commercant} />
        </td>
      ) : null}
      <td className={mesCmdTdMain()}>
        <div className="flex items-center gap-3 min-w-0">
          <MesCommandesItemThumb
            variant="rdv"
            size="maquette"
            imageUrl={prestationImageUrl}
            accentColor={prestationAccent}
            alt={prestationLabel || 'Prestation'}
          />
          <div className="min-w-0">
            <div className="font-bold text-[15px] text-gray-900 leading-snug line-clamp-2" title={prestationLabel || ''}>
              {prestationLabel || 'Prestation'}
            </div>
            {prestationSubtitle ? (
              <div className="text-xs text-gray-500 mt-0.5 truncate" title={prestationSubtitle}>
                {prestationSubtitle}
              </div>
            ) : null}
          </div>
        </div>
      </td>
      <td className={`${mesCmdTdDuree()} font-bold text-gray-800 text-[15px]`}>{rdvDureeLabel || '—'}</td>
      <td className={mesCmdTdTotal()}>
        <div className="font-bold text-lg text-gray-900">{ctx.formatEuro(totalAmount)}</div>
      </td>
      <td className={mesCmdTdStatut()}>
        <StatusPill statusDisplay={statusDisplay} statut={commande.statut} />
      </td>
      <td className={mesCmdTdPaiement()}>
        <div className="mes-cmd-payment-chip max-w-full [&_*]:text-xs">
          <MesCommandesRdvPaymentCell commande={commande} />
        </div>
      </td>
      <td className={mesCmdTdDate()}>
        <MesCommandesRdvDatesCell commande={commande} />
      </td>
      <td className={mesCmdTdActions()}>
        <RowActions
          commande={commande}
          ctx={ctx}
          onOpenDetail={onOpenDetail}
          onProductStatus={onProductStatus}
          onProductCancel={onProductCancel}
          onRdvAction={onRdvAction}
        />
      </td>
    </tr>
  );
}

function MaquetteTableHead({ labels, ColGroup }) {
  const total = labels.length;
  return (
    <>
      {ColGroup ? <ColGroup /> : null}
      <thead className="bg-gray-50 border-b-2 border-gray-200/80">
        <tr>
          {labels.map((label, index) => (
            <th key={label} className={mesCmdHeadClass(index, total)}>
              {label}
            </th>
          ))}
        </tr>
      </thead>
    </>
  );
}

export function MesCommandesRdvTableHead({ showCommercantColumn = false } = {}) {
  const labels = [
    MES_COMMANDES_PRESTATIONS_RDV_TABLE_REF_LABEL,
    'Client',
    ...(showCommercantColumn ? ['Commerçant'] : []),
    'Prestation',
    'Durée',
    'Total',
    'Statut',
    'Paiement',
    'Dates',
    'Actions',
  ];
  const ColGroup = showCommercantColumn ? MesCommandesPlatformRdvColGroup : MesCommandesRdvColGroup;
  return <MaquetteTableHead ColGroup={ColGroup} labels={labels} />;
}

export function MesCommandesProductTableHead({ showCommercantColumn = false } = {}) {
  const labels = [
    'Commande',
    'Client',
    ...(showCommercantColumn ? ['Commerçant'] : []),
    'Produits',
    'Total',
    'Statut',
    'Paiement',
    'Date',
    'Actions',
  ];
  const ColGroup = showCommercantColumn ? MesCommandesPlatformProductColGroup : MesCommandesProductColGroup;
  return <MaquetteTableHead ColGroup={ColGroup} labels={labels} />;
}
